import React, { useState } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import {
  Loader2, X, Tv, Smartphone, Copy, ExternalLink, Check
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;
// The QR points the user's phone to the mobile Remote page — so we want the
// *browser-visible* origin (e.g. https://cccvob.com) rather than the backend
// API host (which may be a *.emergent.host internal URL on production).
const BASE_URL = (typeof window !== 'undefined' && window.location?.origin)
  ? window.location.origin
  : (process.env.REACT_APP_BACKEND_URL || '');

const ControlPanel = ({ currentDisplay, onRefresh, hymns, announcements }) => {
  const [loading, setLoading] = useState(null);
  const [remoteCode, setRemoteCode] = useState('');
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleAction = async (action, data = {}) => {
    setLoading(action);
    try {
      await axios.post(`${API}/display/show/${action}`, data);
      onRefresh();
    } catch (error) {
      console.error(`Error showing ${action}:`, error);
      alert(`Failed to show ${action}`);
    }
    setLoading(null);
  };

  const generateRemoteCode = async () => {
    setRemoteLoading(true);
    try {
      const res = await axios.post(`${API}/display/remote/create-session`);
      if (res.data?.code) setRemoteCode(res.data.code);
    } catch (error) {
      console.error('Error creating remote session:', error);
      alert('Failed to create remote session');
    }
    setRemoteLoading(false);
  };

  const remoteUrl = remoteCode ? `${BASE_URL}/remote?code=${remoteCode}` : '';

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const controlButtons = [
    { id: 'welcome', label: 'Welcome Screen', icon: Tv, color: 'bg-blue-500 hover:bg-blue-600' },
    { id: 'blank', label: 'Clear Screen', icon: X, color: 'bg-slate-500 hover:bg-slate-600' }
  ];

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-slate-800">Quick Controls</h3>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {controlButtons.map(btn => (
          <button
            key={btn.id}
            data-testid={`control-${btn.id}-btn`}
            onClick={() => handleAction(btn.id)}
            disabled={loading === btn.id}
            className={`flex flex-col items-center gap-3 p-6 ${btn.color} text-white rounded-2xl transition-all hover:scale-105`}
          >
            {loading === btn.id ? (
              <Loader2 className="w-8 h-8 animate-spin" />
            ) : (
              <btn.icon className="w-8 h-8" />
            )}
            <span className="font-medium">{btn.label}</span>
          </button>
        ))}
      </div>

      <div className="p-4 bg-slate-50 rounded-xl">
        <h4 className="font-medium text-slate-700 mb-2">Current Display</h4>
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${currentDisplay?.display_type !== 'blank' ? 'bg-green-500' : 'bg-slate-400'}`} />
          <span className="text-slate-600 capitalize">
            {currentDisplay?.display_type || 'None'} 
            {currentDisplay?.content?.title && ` - ${currentDisplay.content.title}`}
          </span>
        </div>
      </div>

      {/* Remote Control Section */}
      <div className="p-5 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200" data-testid="remote-control-section">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 rounded-lg bg-purple-500/10">
            <Smartphone className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h4 className="font-semibold text-purple-800">Remote Control</h4>
            <p className="text-xs text-purple-600">Control the display from your phone</p>
          </div>
        </div>

        {!remoteCode ? (
          <button
            data-testid="generate-remote-code-btn"
            onClick={generateRemoteCode}
            disabled={remoteLoading}
            className="w-full py-3 rounded-xl bg-purple-600 text-white font-bold hover:bg-purple-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
          >
            {remoteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />}
            Generate Remote Code
          </button>
        ) : (
          <div className="space-y-3">
            {/* QR Code */}
            <div className="flex justify-center p-4 bg-white rounded-xl border border-purple-200" data-testid="remote-qr-code">
              <QRCodeSVG value={remoteUrl} size={180} level="M" includeMargin={false}
                bgColor="#ffffff" fgColor="#6b21a8" />
            </div>
            <p className="text-center text-xs text-purple-600">Scan with your phone camera to connect</p>

            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-purple-200">
              <span className="text-2xl font-mono font-bold text-purple-700 tracking-[0.3em] flex-1 text-center" data-testid="remote-code-display">
                {remoteCode}
              </span>
              <button
                data-testid="copy-remote-code-btn"
                onClick={() => copyToClipboard(remoteCode)}
                className="p-2 rounded-lg hover:bg-purple-100 transition-colors"
                title="Copy code"
              >
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-purple-600" />}
              </button>
            </div>
            <div className="flex gap-2">
              <button
                data-testid="copy-remote-link-btn"
                onClick={() => copyToClipboard(remoteUrl)}
                className="flex-1 py-2 rounded-lg bg-white border border-purple-200 text-purple-700 text-sm font-medium hover:bg-purple-50 transition-all flex items-center justify-center gap-2"
              >
                <Copy className="w-3.5 h-3.5" /> Copy Link
              </button>
              <a
                href={remoteUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="open-remote-link-btn"
                className="flex-1 py-2 rounded-lg bg-white border border-purple-200 text-purple-700 text-sm font-medium hover:bg-purple-50 transition-all flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Open Remote
              </a>
            </div>
            <button
              onClick={generateRemoteCode}
              className="w-full py-2 rounded-lg text-sm text-purple-600 hover:bg-purple-100 transition-colors"
            >
              Generate New Code
            </button>
          </div>
        )}
      </div>

      <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
        <h4 className="font-medium text-amber-800 mb-2">How to use:</h4>
        <ol className="text-sm text-amber-700 space-y-1 list-decimal list-inside">
          <li>Click "Open Live Display" to open the projection screen</li>
          <li>Move that window to your projector/TV screen</li>
          <li>Press F11 for full-screen mode</li>
          <li>Use the controls here to change what's displayed</li>
          <li>Generate a Remote Code to control from your phone</li>
        </ol>
      </div>
    </div>
  );
};

export default ControlPanel;
